/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/offline/syncManager.ts
 *
 * Rôle :
 *   Drain automatique de la queue offline (`src/offline/queue.ts`) dès que
 *   le navigateur retrouve la connexion. Logique :
 *     1. Au montage initial : purge des items expirés + tentative de drain
 *        (cas : on rouvre l'app après une période hors ligne).
 *     2. Listener `online` : drain immédiat.
 *     3. Listener `visibilitychange` (visible) : drain si hors ligne il y
 *        a < 5 min.
 *     4. Polling fallback : check toutes les 60 s en foreground.
 *
 *   Pour chaque item drainé :
 *     - markSyncing(id)
 *     - fetch avec headers `Idempotency-Key` (cf. §18.7)
 *     - 2xx           → remove(id)
 *     - 4xx définitif → markFailed(id) + toast d'erreur utilisateur
 *     - 5xx / réseau  → markPending avec tries+1, retry exponentiel
 *
 *   Réutilisable : 100 %. Pattern d'orchestration générique.
 *
 * Dépendances :
 *   - src/offline/queue.ts
 *   - src/api/client.ts (constante API_BASE_URL via VITE_API_BASE_URL)
 *   - src/stores/toast.ts (notification utilisateur en cas d'échec)
 *
 * Voir : CLAUDE.md §18 (offline-first), §15.5 (wording français).
 *
 * @author Mika
 * @since 2026-05-04
 */

import {
  listPending,
  markFailed,
  markPending,
  markSyncing,
  purgeExpired,
  remove,
  type QueueItem,
} from './queue';
import { toast } from '@/stores/toast';

/** Nombre max de tentatives avant de marquer un item `failed` (cf. §18). */
const MAX_TRIES = 5;

/** Délai exponentiel cap 30 s — utilisé pour rate-limiter les retries. */
function backoffMs(tries: number): number {
  return Math.min(30_000, 500 * 2 ** tries);
}

let draining = false;
let pollingTimer: number | null = null;
let listenersWired = false;

/** État courant exposé pour l'UI (utilisé par useOnlineStatus / OfflineBanner). */
const state = {
  draining: false,
  pendingCount: 0,
};

/**
 * Snapshot **caché** retourné par getSyncSnapshot.
 *
 * FIX 2026-05-07 (debug React #185 prod) — useSyncExternalStore appelle
 * `getSnapshot` plusieurs fois par render et compare le résultat avec
 * `Object.is`. Si on retournait `{ ...state }` à chaque appel (ancienne
 * implémentation), la référence changeait à chaque appel → React détectait
 * un changement permanent → re-render infini → "Maximum update depth
 * exceeded" (#185 minified). Solution : maintenir un snapshot stable, ne
 * créer un nouvel objet QUE dans `notify()` quand l'état change vraiment.
 * Référence officielle : https://react.dev/reference/react/useSyncExternalStore#im-getting-an-error-the-result-of-getsnapshot-should-be-cached
 */
let cachedSnapshot: { readonly draining: boolean; readonly pendingCount: number } = Object.freeze({
  draining: false,
  pendingCount: 0,
});

const listeners = new Set<() => void>();

/**
 * Notifie les abonnés ET met à jour le snapshot caché.
 *
 * Tous les sites de mutation de `state` doivent passer par cette fonction
 * pour garantir que le snapshot exposé reste cohérent. Si rien n'a changé
 * (state strictement identique au snapshot précédent), on ne recrée pas
 * d'objet — utile pour éviter un re-render React inutile en cas d'appel
 * notify "défensif".
 */
function notify(): void {
  if (
    cachedSnapshot.draining !== state.draining ||
    cachedSnapshot.pendingCount !== state.pendingCount
  ) {
    cachedSnapshot = Object.freeze({
      draining: state.draining,
      pendingCount: state.pendingCount,
    });
  }
  for (const l of listeners) l();
}

export function subscribeSyncState(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getSyncSnapshot(): { readonly draining: boolean; readonly pendingCount: number } {
  return cachedSnapshot;
}

/**
 * Démarre le sync manager : pose les listeners, purge les expirés,
 * tente un premier drain. À appeler une seule fois (depuis main.tsx).
 */
export async function startSyncManager(): Promise<void> {
  if (listenersWired) return;
  listenersWired = true;

  // Purge des items expirés (TTL §18.5).
  try {
    const purged = await purgeExpired();
    if (purged > 0) {
      // info silencieux — pas de toast pour ne pas inquiéter
      console.info(`[TNE 2026] Purge offline : ${purged} item(s) expiré(s) retiré(s).`);
    }
  } catch (e) {
    console.warn('[TNE 2026] Purge offline KO :', e);
  }

  // Drain initial (cas reprise app après période hors ligne).
  void drainOnce();

  // Listener réseau natif.
  window.addEventListener('online', () => {
    void drainOnce();
  });
  window.addEventListener('offline', () => {
    // Pas de drain pendant qu'on est offline — juste notifier l'UI.
    notify();
  });

  // Listener visibilité — utile quand l'utilisateur revient de l'arrière-plan.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      void drainOnce();
    }
  });

  // Polling fallback — utile si les events `online` ne déclenchent pas
  // (certains scénarios réseau bizarres, captive portals, etc.).
  pollingTimer = window.setInterval(() => {
    if (navigator.onLine && !draining) {
      void drainOnce();
    }
  }, 60_000);
}

/** Stoppe le manager (utile pour les tests). */
export function stopSyncManager(): void {
  if (pollingTimer !== null) {
    window.clearInterval(pollingTimer);
    pollingTimer = null;
  }
  // On ne désinscrit pas les listeners DOM ici — `startSyncManager` est
  // pensé pour vivre toute la durée de l'app.
  listenersWired = false;
}

/**
 * Tente de drainer la queue maintenant. Appelable manuellement depuis
 * un bouton "Synchroniser maintenant" (admin / paramètres).
 *
 * @returns nombre d'items réussis
 */
export async function drainOnce(): Promise<number> {
  if (draining) return 0;
  draining = true;
  state.draining = true;
  notify();

  let success = 0;
  try {
    const pending = await listPending(50);
    state.pendingCount = pending.length;
    notify();

    for (const item of pending) {
      const ok = await trySend(item);
      if (ok) success += 1;
      // Petite pause respectueuse entre items pour ne pas marteler le serveur.
      await sleep(150);
    }
  } finally {
    draining = false;
    state.draining = false;
    state.pendingCount = (await listPending(1)).length;
    notify();
  }
  return success;
}

/**
 * Tente d'envoyer un item. Renvoie true en cas de succès (et l'item est
 * supprimé de la queue), false sinon.
 */
async function trySend(item: QueueItem): Promise<boolean> {
  const tries = item.tries + 1;
  // Rate-limit basique : ne pas marteler un item qui vient juste d'échouer.
  if (item.lastTryAt !== null) {
    const wait = backoffMs(item.tries) - (Date.now() - item.lastTryAt);
    if (wait > 0) {
      // Pas mûr — on saute, sera retenté au prochain drain.
      return false;
    }
  }

  await markSyncing(item.id);

  const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
  const url = `${baseUrl}/${item.path.replace(/^\//, '')}`;

  try {
    const res = await fetch(url, {
      method: item.method,
      headers: {
        Accept: 'application/json',
        'Accept-Language': 'fr-FR',
        'Content-Type': 'application/json',
        'Idempotency-Key': item.idempotencyKey,
        'X-Requested-With': 'TNE-2026',
      },
      body: item.body || undefined,
      credentials: 'same-origin',
    });

    if (res.ok) {
      await remove(item.id);
      return true;
    }

    // 4xx = erreur définitive (validation, ressource manquante, conflit
    // métier — ex. atelier complet entre-temps).
    if (res.status >= 400 && res.status < 500) {
      await markFailed(item.id, `HTTP ${res.status}`, tries);
      toast.error(humanReasonFor(item, res.status));
      return false;
    }

    // 5xx → on retry plus tard si on n'a pas dépassé MAX_TRIES.
    if (tries >= MAX_TRIES) {
      await markFailed(item.id, `HTTP ${res.status} après ${tries} tentatives`, tries);
      toast.error(`Synchronisation impossible — ${humanReasonFor(item, res.status)}`);
      return false;
    }
    await markPending(item.id, tries);
    return false;
  } catch (e) {
    // Erreur réseau (TypeError de fetch) — on retry plus tard.
    if (tries >= MAX_TRIES) {
      const msg = e instanceof Error ? e.message : 'Erreur inconnue';
      await markFailed(item.id, msg, tries);
      toast.error(
        `Action en attente abandonnée après ${tries} tentatives (${humanKind(item.kind)}).`,
      );
      return false;
    }
    await markPending(item.id, tries);
    return false;
  }
}

/** Wording français pour les raisons d'échec — cf. §15.5 + i18n. */
function humanReasonFor(item: QueueItem, status: number): string {
  const kind = humanKind(item.kind);
  if (status === 409) return `Conflit sur ${kind} (déjà à jour ou place prise).`;
  if (status === 401 || status === 403) return `Non autorisé sur ${kind}.`;
  if (status === 404) return `Ressource introuvable pour ${kind}.`;
  if (status === 422) return `Données invalides pour ${kind}.`;
  if (status >= 500) return `Erreur serveur sur ${kind}.`;
  return `Échec sur ${kind} (HTTP ${status}).`;
}

/** Mapping `kind` interne → wording utilisateur. */
function humanKind(kind: string): string {
  switch (kind) {
    case 'checkin':
      return 'pointage stand';
    case 'workshop_signup':
      return 'inscription atelier';
    case 'workshop_cancel':
      return 'désinscription atelier';
    case 'profile_update':
      return 'mise à jour profil';
    case 'consent':
      return 'consentement RGPD';
    case 'feedback':
      return 'retour utilisateur';
    default:
      return kind;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
